import { useEffect, useState, useCallback } from "react";
import { api, API } from "./api";

// Resolve an image value: absolute URLs pass through; storage paths are served via the backend.
export function imgSrc(val) {
  if (!val) return "";
  if (/^https?:\/\//i.test(val) || val.startsWith("data:")) return val;
  return `${API}/files/${val}`;
}

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/41ef69e5-e4fb-4fd7-8f79-f334d2b03553/images/d7abb13dc339e3a25dc8caf65fcbc0710665dc5c378162ded4a0c9c4c5b1bd80.png";
const FOUNDER_BG =
  "https://static.prod-images.emergentagent.com/jobs/41ef69e5-e4fb-4fd7-8f79-f334d2b03553/images/c405163be7cd45258c2ce74186e94458b8f81d246427cf04a115c5286a9ef0f0.png";

// Full default content. Admin edits are deep-merged over this.
export const DEFAULT_CONTENT = {
  brand: {
    tagline:
      "A design & engineering studio founded and operated by Vedant Baligar. Custom presentations, software, AI agents, 3D models, animated websites, and cinematic videos for ambitious brands.",
  },
  hero: {
    eyebrow: "Xion Labs / Design • Engineering • Motion",
    heading_pre: "We craft",
    heading_accent1: "interfaces",
    heading_mid: "stories, and",
    heading_accent2: "cinematic",
    heading_post: "products.",
    subtitle:
      "Custom presentations, software, AI agents, 3D models, animated websites and videos — engineered by Xion Labs for ambitious brands and founders. Tell us what you need, we ship pixel-perfect, on time.",
    cta_primary: "Start a project",
    cta_secondary: "See the work",
    image: HERO_BG,
  },
  stats: [
    { value: "", label: "Projects shipped" },
    { value: "", label: "Avg. first draft" },
    { value: "", label: "Client rating" },
    { value: "", label: "Service lines" },
  ],
  services_section: {
    eyebrow: "/ services",
    title_pre: "Services.",
    title_accent: "One studio",
    cta: "Explore all services",
  },
  portfolio_section: {
    eyebrow: "/ work",
    title_pre: "Recent",
    title_accent: "obsessions.",
  },
  founder: {
    eyebrow: "/ the operator",
    initials: "VB",
    name: "Vedant Baligar",
    role: "/ founder & operator",
    short: "Designing, building, and shipping every Xion Labs project — personally.",
    heading_pre: "One",
    heading_accent1: "studio",
    heading_mid: ". One operator.",
    heading_accent2: "Zero",
    heading_post: "middlemen.",
    bio: "Xion Labs is founded and operated by Vedant Baligar. Every brief lands directly in his inbox. Every milestone is delivered with his signature. When you hire Xion Labs, you hire Vedant — no account managers, no subcontractors, no compromises.",
    stat1_v: "100%",
    stat1_l: "Direct contact",
    stat2_v: "24h",
    stat2_l: "Reply window",
    image: FOUNDER_BG,
  },
  cta_section: {
    title_pre: "Ready to build the",
    title_accent: "future",
    title_post: "of your brand?",
    subtitle: "Tell us what you need. We respond within 24 hours with a tailored plan.",
    button: "Request a Quote",
  },
  services_page: {
    eyebrow: "/ services",
    title_pre: "Ways to",
    title_accent: "make the leap",
    subtitle:
      "Every Xion Labs service is custom-crafted from first principles. Pick a category — we'll tailor the scope, timeline, and delivery to match your exact goal.",
  },
  portfolio_page: {
    eyebrow: "/ work",
    title_pre: "Selected",
    title_accent: "work.",
    subtitle: "A small slice of what we've shipped for founders, studios, and brands.",
  },
};

function deepMerge(base, override) {
  if (Array.isArray(base)) {
    return Array.isArray(override) && override.length ? override : base;
  }
  if (base && typeof base === "object") {
    const out = { ...base };
    if (override && typeof override === "object") {
      for (const k of Object.keys(override)) {
        out[k] = deepMerge(base[k] !== undefined ? base[k] : override[k], override[k]);
      }
    }
    return out;
  }
  return override !== undefined && override !== null ? override : base;
}

export function mergeContent(fetched) {
  return deepMerge(DEFAULT_CONTENT, fetched || {});
}

// Public hook: loads content and deep-merges over defaults.
export function useSiteContent() {
  const [content, setContent] = useState(() => mergeContent({}));
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setContent(mergeContent(data));
    } catch (e) {
      setContent(mergeContent({}));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { content, loaded, reload };
}
